import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { CheckGroupPermissions } from "@helpers/checkGroupPermissions";
import { PartImageUrl } from "@helpers/partImage";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { DeleteObject } from "@interfaces/dto/deleteObject.interface";
import { ImportCSV } from "@interfaces/importCSV.interface";
import { LogExport } from "@interfaces/logExport.interface";
import { Client } from "@interfaces/models/client.interface";
import { Project } from "@interfaces/project.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { ClientService } from "@services/client.service";
import { ExportLogService } from "@services/exportLog.service";
import { FileService } from "@services/file.service";
import { PartService } from "@services/part.service";
import { ProjectService } from "@services/project.service";
import { ProtectedGridService } from "@services/protectedGrid.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "parts",
	styleUrls: ["parts.css"],
	templateUrl: "parts.html"
})
export class PartsAdminComponent {
	canExportGrid = false;
	clients: Client[] = [];
	deleteEnabled = true;
	editEnabled = true;
	gridLoading = false;
	headerPrimaryButtons: AdminHeaderButton[] = [
		{ icon: "fal fa-plus", method: "addNewItem", text: "New Part" },
		{ icon: "fal fa-upload", method: "importCSVPopup", text: "Import Parts CSV" }
	];
	headerSecondaryButtons: AdminHeaderButton[] = [
		{ icon: "fal fa-calculator", method: "calculateStatisticsForCompletedParts", text: "Calculate Statistics" },
		{ icon: "fal fa-download", method: "exportPartTimingsCSV", text: "Export Part Timings CSV" },
		{ icon: "fal fa-upload", method: "importPartTimingsCSVPopup", text: "Import Part Timings CSV" }
	];
	isSuperAdmin = false;
	itemType = "Part";
	itemUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	items: any[] = [];
	popupAddButtonOptions: any = { onClick: () => this.importCSV(), text: "Import CSV" };
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupDeleteObject: DeleteObject = new DeleteObject();
	popupImportDetails: ImportCSV = new ImportCSV();
	popupTitle = "Import CSV";
	popupVisible = false;
	projects: Project[] = [];
	title: string;
	validationErrors: string[] = [];
	
	constructor(private appSettingsService: AppSettingsService, private router: Router, private toastService: ToastService, private itemService: PartService, private clientService: ClientService, private exportLogService: ExportLogService, private fileService: FileService, private projectService: ProjectService, private protectedGridService: ProtectedGridService) {
		this.title = `${this.itemType}s`;
		this.deleteRowPopup = this.deleteRowPopup.bind(this);
		this.duplicatePart = this.duplicatePart.bind(this);
		this.editRow = this.editRow.bind(this);
		this.uploadCSVForImport = this.uploadCSVForImport.bind(this);
		this.viewRow = this.viewRow.bind(this);
		this.getItems();
		this.getClients();
		this.getProjects();
		this.checkGridExportPermissions();
		
		this.isSuperAdmin = CheckGroupPermissions(appSettingsService, "Super Admin");
		if (this.isSuperAdmin) {
			this.headerPrimaryButtons.push({ method: "viewDeleted", text: "Deleted Parts" });
		}
	}

	addNewItem() {
		this.router.navigate([`${this.itemUrl}/create`]);
	}

	boolYesNo(cellInfo: any){		
		if(cellInfo.value === true)
		{
			return "YES";
		}
		else
		{
			return "NO";
		}
	}

	calculateStatisticsForCompletedParts() {
		if (confirm("Are you sure you wish to calculate statistics for all completed parts (i.e. parts where 'needs completing' is off)? Please note this may produce odd results depending on whether accurate information has been entered into the router and ops audits.") === false) {
			return;
		}
		this.itemService.calculateStatisticsForAllCompleted()
			.subscribe(
				() => {
					this.toastService.success("Successfully Calculated Statistics");
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}
	
	checkGridExportPermissions() {
		this.protectedGridService.canExport("Parts")
			.subscribe(
				(res: any) => {
					this.canExportGrid = res.response;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}
	
	closePopup() {
		this.popupVisible = false;
	}

	deleteRow() {
		if (this.popupDeleteObject.reason === "") {
			alert("You must specify a reason for deletion.");
			return;
		}
		this.itemService.delete(this.popupDeleteObject).subscribe(
			() =>{
				this.toastService.success("Successfully Deleted");
				this.closePopup();
				this.getItems();
			},
			(err) => {
				console.log(err);
				this.toastService.error("Something Went Wrong!");
			},
		);
	}
	
	deleteRowPopup(e: any) {
		this.popupTitle = "Delete Part";
		this.popupDeleteObject = new DeleteObject();
		this.popupDeleteObject.id = e.row.data.id;
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.deleteRow(), text: "Delete" };
		return false;
	}

	duplicatePart(e: any) {
		if (confirm("Are you sure you wish to duplicate this part? You will be taken to it for editing.") === true)
		{
			this.itemService.duplicate(e.row.data.id)
				.subscribe(
					(res: any) => {
						this.toastService.success(this.itemType + " duplicated!");
						this.router.navigate([`${this.itemUrl}/${res.response.id}_edit`]);
					},
					(err) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					},
				);
		}
	}

	editRow(e: any) {
		this.router.navigate([`${this.itemUrl}/${e.row.data.id}_edit`]);
	}

	exportPartTimingsCSV() {
		this.itemService.exportTimingsCSV().subscribe(
			(response: any) =>{
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
				downloadLink.setAttribute("download", "PartTimings.csv");
				document.body.appendChild(downloadLink);
				downloadLink.click();
			}
		);
	}

	getClients() {
		this.clientService.getAll()
			.subscribe(
				(res: any) => this.clients = res.response.items,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	getItems() {
		this.gridLoading = true;
		this.itemService.getAll()
			.subscribe(
				(res: any) => {
					this.items = res.response.items;
					this.gridLoading = false;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	// Shared with the tools grid so both show a part the same way.
	getPartImage(imageUrl: string) {
		return PartImageUrl(imageUrl);
	}

	getProjects() {
		this.projectService.getAll()
			.subscribe(
				(res: any) => this.projects = res.response.items,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	importCSV() {
		this.validationErrors = [];
		this.itemService.importCSV(this.popupImportDetails)
			.subscribe(
				() => {
					this.closePopup();
					this.getItems();
				},
				(err: any) => {
					this.validationErrors = err.file;
				}
			);
	}

	importCSVPopup() {
		this.validationErrors = [];
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.importCSV(), text: "Import CSV" };
		this.popupCloseButtonOptions = { onClick: () => this.closePopup(), text: "Close" };
		this.popupImportDetails = new ImportCSV();
		this.popupTitle = "Import CSV";
	}

	importPartTimingsCSV() {
		this.validationErrors = [];
		this.itemService.importTimingsCSV(this.popupImportDetails)
			.subscribe(
				() => {
					this.closePopup();
				},
				(err: any) => {
					this.validationErrors = err.file;
				}
			);
	}

	importPartTimingsCSVPopup() {
		this.validationErrors = [];
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.importPartTimingsCSV(), text: "Import Part Timings CSV" };
		this.popupCloseButtonOptions = { onClick: () => this.closePopup(), text: "Close" };
		this.popupImportDetails = new ImportCSV();
		this.popupTitle = "Import Part Timings CSV";
	}

	logExport() {
		const logExport = new LogExport();
		logExport.gridName = "Parts";
		this.exportLogService.logExport(logExport)
			.subscribe(
				() => console.log("Export Logged"),
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
					return false;
				},
			);
	}
	
	onRowPrepared(e: any) {
		if (e.rowType === "data") {  
			if (e.data.needsCompleting === true)  {
				e.rowElement.classList.add("needsCompleting");
				e.rowElement.style.backgroundColor = "#ffd2ad";  
				e.rowElement.className = e.rowElement.className.replace("dx-row-alt", "");
			}
		}  
	}

	uploadCSVForImport(file: File) {
		this.validationErrors = [];
		const containerName = "parts-imports";
		const uploadData = new FormData();
		uploadData.append("containerName", containerName);
		uploadData.append("files", file);
		this.fileService.uploadFile(uploadData)
			.subscribe(
				(res: any) => {
					this.popupImportDetails.fileLookupId = res.response[0].lookupId;
				},
				(err) => {
					console.log(err);
					this.toastService.error("CSV Upload Failed - Please ensure you are uploading a valid file");
				}
			);
	}

	viewDeleted() {
		this.router.navigate([`${this.itemUrl}/deleted`]);
	}

	viewRow(e: any) {
		this.router.navigate([`${this.itemUrl}/${e.row.data.id}`]);
	}
}